import type { SyntheticEvent } from "react";
import type { CartMap, Product, RecommendationItem } from "../../types";
import QuantityInput from "../catalog/QuantityInput";
import {
  resolveMinQuantitySimple,
  resolveStep,
} from "../../utils/productQuantity";
import {
  getRecommendationCartValues,
  getRecommendationPriceLabel,
  getRecommendationTextValues,
} from "../../utils/recommendationItem";

interface RecommendedProductCardProps {
  item: RecommendationItem;
  cart?: CartMap;
  onQuantityChange?: (product: Product, value: number) => void;
  baseMinQuantity?: number;
  imageUrls?: Record<number, string>;
}

const RecommendedProductCard = ({
  item,
  cart = {},
  onQuantityChange,
  baseMinQuantity = 0,
  imageUrls = {},
}: RecommendedProductCardProps) => {
  const product = item.product;
  const { description, availabilityLabel } = getRecommendationTextValues(item);
  const priceLabel = getRecommendationPriceLabel(item);
  const { quantity, canEdit } = getRecommendationCartValues({
    product,
    cart,
    onQuantityChange,
  });
  const imageUrl = product ? imageUrls[product.id] : null;

  return (
    <article className="product-card">
      {imageUrl && (
        <div className="product-image-container">
          <img
            src={imageUrl}
            alt={item.name}
            className="product-image"
            onError={(event: SyntheticEvent<HTMLImageElement>) => {
              if (event.currentTarget.parentElement) {
                event.currentTarget.parentElement.style.display = "none";
              }
            }}
          />
        </div>
      )}
      <header>
        <h3>{item.name}</h3>
        {priceLabel && <span className="price">{priceLabel}</span>}
      </header>
      <p className="description">{description}</p>
      {availabilityLabel && <p className="availability">{availabilityLabel}</p>}
      {canEdit && product && onQuantityChange && (
        <QuantityInput
          value={quantity}
          onChange={(value) => onQuantityChange(product, value)}
          minQuantity={resolveMinQuantitySimple(product, baseMinQuantity)}
          step={resolveStep(product)}
        />
      )}
    </article>
  );
};

export default RecommendedProductCard;
